import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import NavigationHelper from 'yoda-core-components/lib/helpers/NavigationHelper/NavigationHelper';
import { getDeviceType as getDeviceTypeSelector } from '../../selectors/CMPageSelector';
import { selectFeatureFlags } from '../../selectors/ContextSelector';
import HeroHeadline from '../CMHeroDeltas/HeroHeadline';
import HeroSubHeadline from '../CMHeroDeltas/HeroSubHeadline';
import HeroLegalText from '../CMHeroDeltas/HeroLegalText';
import HeroImage from '../CMHeroDeltas/HeroImage';
import HeroCTA from '../CMHeroDeltas/HeroCTA';
import { Link } from '../Link/Link';
import { heroAutomationIds } from '../CoreMediaPage/CMPage.config';

const stylingConfig = {
    priSecPadding: {
        None: 'md:gap-0',
        S: 'md:gap-2',
        M: 'md:gap-4',
    },
    overAllPaddingWithBG: {
        None: 'md:p-4',
        S: 'md:p-2',
        M: 'md:p-4',
    },
    secondaryPadding: {
        None: 'md:gap-0',
        S: 'md:gap-2',
        M: 'md:gap-4',
    },
};

const CMHero3 = (props) => {
    const data = get(props, 'data', props);
    const automationId = get(data, 'automationId', '');
    const deviceType = useSelector((state) => getDeviceTypeSelector(state));
    const [isSmallTablet, setIsSmallTablet] = useState(false);
    const featureFlags = useSelector((state) => selectFeatureFlags(state));
    useEffect(() => {
        const isSmallScreen = !__SERVER__ && deviceType.isTablet && window.innerWidth <= 767;
        if (isSmallScreen !== isSmallTablet) {
            setIsSmallTablet(isSmallScreen);
        }
    }, [__SERVER__]);
    const renderHeroImage = (isPrimary, item) => {
        const ctaLinks = isPrimary ? get(item, 'ctaLinks', []) : [item.cta];
        const media = get(item, 'media', []);
        const messageLockUpAlignment = get(item, 'styles.messageLockUpAlignment', '');
        return (
            <div className={dt(['smOnly:mb-1', isSmallTablet ? '' : 'h-full'])}>
                <HeroImage
                    heroType={isPrimary ? 'primary' : 'secondary'}
                    ctaLinks={ctaLinks}
                    media={media}
                    messageLockUpAlignment={messageLockUpAlignment}
                    isPrimary={isPrimary}
                />
            </div>
        );
    };
    const shouldScaledDownFont = deviceType.isTablet && featureFlags.enableHero3ScaledDownLockup;
    const renderHeadline = (isPrimary, item) => {
        const teaserTitle = get(item, 'teaserTitle', '');
        const itemStyles = get(item, 'styles', '');
        return (
            <HeroHeadline
                teaserTitle={teaserTitle}
                styles={itemStyles}
                shouldScaledDownFont={shouldScaledDownFont}
                isPrimary={isPrimary}
                deviceType={deviceType}
            />
        );
    };
    const renderSubHeadline = (isPrimary, item) => {
        const itemStyles = get(item, 'styles', '');
        const teaserSubtitle = get(item, 'teaserSubtitle', '');
        const isLegalTextEmpty = isEmpty(get(item, 'legalText.legalText', ''));
        return (
            <HeroSubHeadline
                teaserSubtitle={teaserSubtitle}
                styles={itemStyles}
                isPrimary={isPrimary}
                deviceType={deviceType}
                isLegalTextEmpty={isLegalTextEmpty}
            />
        );
    };
    const renderLegalText = (isPrimary, item) => {
        const itemStyles = get(item, 'styles', '');
        const legalText = get(item, 'legalText', {});
        let shouldRenderLegalText = true;
        shouldRenderLegalText = !isEmpty(legalText.legalText);
        const ctaLinks = isPrimary ? get(item, 'ctaLinks', []) : [item?.cta];
        let isCTAEmpty = true;
        if (isPrimary) {
            isCTAEmpty = isEmpty(ctaLinks);
        } else {
            isCTAEmpty = isEmpty(get(ctaLinks, '[0].urlHolder.value', ''));
        }
        return shouldRenderLegalText ? (
            <HeroLegalText
                legalText={legalText}
                styles={itemStyles}
                isPrimary={isPrimary}
                isCTAEmpty={isCTAEmpty}
            />
        ) : null;
    };
    const renderCTA = (isPrimary, item) => {
        const componentStyles = get(data, 'styles', '');
        const ctaLinks = isPrimary ? get(item, 'ctaLinks', []) : [item?.cta];
        const messageLockUpAlignment = get(item, 'styles.messageLockUpAlignment', 'left');
        const itemStyles = get(item, 'styles', '');
        let shouldRenderCTA = true;
        if (isPrimary) {
            shouldRenderCTA = !isEmpty(ctaLinks);
        } else {
            shouldRenderCTA = !isEmpty(get(ctaLinks, '[0].urlHolder.value', ''));
        }
        return shouldRenderCTA ? (
            <HeroCTA
                ctaLinks={ctaLinks}
                styles={componentStyles}
                ctaColor={itemStyles.ctaColor}
                isPrimary={isPrimary}
                messageLockUpAlignment={messageLockUpAlignment}
                shouldScaledDownFont={shouldScaledDownFont}
                deviceType={deviceType}
            />
        ) : null;
    };
    const navigateToLinks = (event, url, dataItem) => {
        event.preventDefault();
        const openLinkInNewTab = get(dataItem, 'openInNewTab', '');
        return !__SERVER__ ? NavigationHelper.navigate(url, openLinkInNewTab) : '';
    };
    const getComponentItem = (isPrimary) => {
        let returnItem = null;
        if (deviceType.isMobile) {
            if (isPrimary) {
                const primaryItem = get(data, 'primaryItem', {});
                const cta = get(primaryItem, 'ctaLinks[0]', {});
                const isNavigationLink = !isEmpty(get(cta, 'urlHolder.value', ''));
                const headlinePlacementMobile = get(
                    data,
                    'primaryItem.styles.headlinePlacementMobile',
                    'below'
                );
                returnItem = (
                    <>
                        {headlinePlacementMobile === 'above' &&
                            (isNavigationLink ? (
                                <Link
                                    href={cta?.urlHolder?.value}
                                    openLinkInNewTab={cta?.urlHolder?.openInNewTab}
                                    onClick={(event) => {
                                        navigateToLinks(
                                            event,
                                            cta?.urlHolder?.value,
                                            cta?.urlHolder
                                        );
                                    }}
                                >
                                    {renderHeadline(isPrimary, primaryItem)}
                                </Link>
                            ) : (
                                renderHeadline(isPrimary, primaryItem)
                            ))}
                        {renderHeroImage(isPrimary, primaryItem)}
                        {headlinePlacementMobile === 'below' &&
                            (isNavigationLink ? (
                                <Link
                                    href={cta?.urlHolder?.value}
                                    openLinkInNewTab={cta?.urlHolder?.openInNewTab}
                                    onClick={(event) => {
                                        navigateToLinks(
                                            event,
                                            cta?.urlHolder?.value,
                                            cta?.urlHolder
                                        );
                                    }}
                                >
                                    {renderHeadline(isPrimary, primaryItem)}
                                </Link>
                            ) : (
                                renderHeadline(isPrimary, primaryItem)
                            ))}
                        {renderSubHeadline(isPrimary, primaryItem)}
                        {renderLegalText(isPrimary, primaryItem)}
                        {renderCTA(isPrimary, primaryItem)}
                    </>
                );
            } else {
                const secondaryItems = get(data, 'items', []);
                returnItem = [];
                secondaryItems.forEach((item) => {
                    const cta = get(item, 'cta', {});
                    const isNavigationLink = !isEmpty(get(cta, 'urlHolder.value', ''));
                    returnItem.push(
                        <div className={dt(['smOnly:block', 'smOnly:h-full', 'smOnly:w-full'])}>
                            {renderHeroImage(isPrimary, item)}
                            {isNavigationLink ? (
                                <Link
                                    href={cta?.urlHolder?.value}
                                    openLinkInNewTab={cta?.urlHolder?.openInNewTab}
                                    onClick={(event) => {
                                        navigateToLinks(
                                            event,
                                            cta?.urlHolder?.value,
                                            cta?.urlHolder
                                        );
                                    }}
                                >
                                    {renderHeadline(isPrimary, item)}
                                </Link>
                            ) : (
                                renderHeadline(isPrimary, item)
                            )}
                            {renderSubHeadline(isPrimary, item)}
                            {renderLegalText(isPrimary, item)}
                            {renderCTA(isPrimary, item)}
                        </div>
                    );
                });
            }
        } else {
            const componentContainerStyles = dt(['md:relative', 'md:w-full']);
            const lockUpContainerStyles = dt(['md:absolute', dt(['md:max-w-[calc(50%-40px)]'])]);
            let lockupPositionStyles = '';
            if (isPrimary) {
                const primaryItem = get(data, 'primaryItem', {});
                const cta = get(primaryItem, 'ctaLinks[0]', {});
                const isNavigationLink = !isEmpty(get(cta, 'urlHolder.value', ''));
                const messageLockUpAlignment = get(
                    primaryItem,
                    'styles.messageLockUpAlignment',
                    'top-left'
                );
                switch (messageLockUpAlignment) {
                    case 'top-left':
                        lockupPositionStyles = dt([
                            'lg:top-10',
                            'md:top-6',
                            'lg:left-10',
                            'md:left-6',
                            'text-left',
                        ]);
                        break;
                    case 'top-right':
                        lockupPositionStyles = dt([
                            'lg:top-10',
                            'md:top-6',
                            'lg:right-10',
                            'md:right-6',
                            'text-right',
                        ]);
                        break;
                    case 'middle-left':
                        lockupPositionStyles = dt([
                            'md:top-1/2',
                            'md:left-10',
                            'text-left',
                            'md:!top-[calc(50%-40px)]',
                            'md:!h-[calc(50%+40px)]',
                        ]);
                        break;
                    case 'middle-right':
                        lockupPositionStyles = dt([
                            'md:top-1/2',
                            'md:right-10',
                            'text-right',
                            'md:!top-[calc(50%-40px)]',
                            'md:!h-[calc(50%+40px)]',
                        ]);
                        break;
                    default:
                        break;
                }
                returnItem = (
                    <div className={componentContainerStyles}>
                        {renderHeroImage(isPrimary, primaryItem)}
                        <div
                            id="primaryMessageLockupContainer"
                            className={[lockUpContainerStyles, lockupPositionStyles].join(' ')}
                        >
                            {isNavigationLink ? (
                                <Link
                                    href={cta?.urlHolder?.value}
                                    openLinkInNewTab={cta?.urlHolder?.openInNewTab}
                                    onClick={(event) => {
                                        navigateToLinks(
                                            event,
                                            cta?.urlHolder?.value,
                                            cta?.urlHolder
                                        );
                                    }}
                                >
                                    {renderHeadline(isPrimary, primaryItem)}
                                    {renderSubHeadline(isPrimary, primaryItem)}
                                </Link>
                            ) : null}
                            {!isNavigationLink ? renderHeadline(isPrimary, primaryItem) : null}
                            {!isNavigationLink ? renderSubHeadline(isPrimary, primaryItem) : null}
                            {renderLegalText(isPrimary, primaryItem)}
                        </div>
                        {renderCTA(isPrimary, primaryItem)}
                    </div>
                );
            } else {
                const secondaryItems = get(data, 'items', []);
                returnItem = [];
                secondaryItems.forEach((item, i) => {
                    const cta = get(item, 'cta', {});
                    const isNavigationLink = !isEmpty(get(cta, 'urlHolder.value', ''));
                    const messageLockUpAlignment = get(
                        item,
                        'styles.messageLockUpAlignment',
                        'left'
                    );
                    switch (messageLockUpAlignment) {
                        case 'left':
                            lockupPositionStyles = dt(['md:top-4', 'md:left-4', 'text-left']);
                            break;
                        case 'right':
                            lockupPositionStyles = dt(['md:top-4', 'md:right-4', 'text-right']);
                            break;
                        default:
                            break;
                    }
                    returnItem.push(
                        <div className={componentContainerStyles}>
                            {renderHeroImage(isPrimary, item)}
                            <div
                                className={[lockUpContainerStyles, lockupPositionStyles].join(' ')}
                                id={`secondaryMessageLockupContainer-${i + 1}`}
                            >
                                {isNavigationLink ? (
                                    <Link
                                        href={cta?.urlHolder?.value}
                                        openLinkInNewTab={cta?.urlHolder?.openInNewTab}
                                        onClick={(event) => {
                                            navigateToLinks(
                                                event,
                                                cta?.urlHolder?.value,
                                                cta?.urlHolder
                                            );
                                        }}
                                    >
                                        {renderHeadline(isPrimary, item)}
                                        {renderSubHeadline(isPrimary, item)}
                                    </Link>
                                ) : null}
                                {!isNavigationLink ? renderHeadline(isPrimary, item) : null}
                                {!isNavigationLink ? renderSubHeadline(isPrimary, item) : null}
                                {renderLegalText(isPrimary, item)}
                            </div>
                            {renderCTA(isPrimary, item)}
                        </div>
                    );
                });
            }
        }
        return returnItem;
    };
    const primaryItemPlacement = get(data, 'styles.primaryItemPlacement', 'left');
    const primaryComponent = getComponentItem(true);
    const secondaryComponentsArray = getComponentItem(false);
    const itemPadding = get(data, 'styles.itemPadding', 'None');
    const componentContainerBackgroundColor = `#${get(
        data,
        'styles.componentBackgroundColor',
        ''
    )}`;
    const primaryPadding = !deviceType.isMobile
        ? get(stylingConfig, `priSecPadding.${itemPadding}`)
        : '';
    const secondaryPadding = !deviceType.isMobile
        ? get(stylingConfig, `secondaryPadding.${itemPadding}`)
        : '';
    const isBGWhite =
        componentContainerBackgroundColor === '#' ||
        componentContainerBackgroundColor === '#None' ||
        componentContainerBackgroundColor === '#ffffff' ||
        componentContainerBackgroundColor === '#fff';
    const componentContainerBGStyles = !isBGWhite
        ? ['smOnly:w-[calc(100%+48px)]', 'smOnly:-mx-6', 'smOnly:p-6']
        : [];
    const overAllPaddingWithBGStyles =
        !isBGWhite && !deviceType.isMobile
            ? get(stylingConfig, `overAllPaddingWithBG.${itemPadding}`)
            : '';

    return (
        <div
            className={dt([
                primaryItemPlacement === 'left' ? 'md:flex-row' : 'md:flex-row-reverse',
                'smOnly:flex-col',
                'flex',
                primaryPadding,
                overAllPaddingWithBGStyles,
                ...componentContainerBGStyles,
                'lg:min-h-[580px]',
                'mdOnly:min-h-[343px]',
            ])}
            style={{ backgroundColor: componentContainerBackgroundColor }}
            data-automation-id={automationId}
        >
            <div
                className={dt(['md:w-8/12', 'md:flex', 'smOnly:block'])}
                data-automation-id={heroAutomationIds?.PRIMARYCONTAINER}
            >
                {primaryComponent}
            </div>
            <div
                className={dt([
                    'md:w-4/12',
                    'flex',
                    'md:flex-col',
                    secondaryPadding,
                    'smOnly:mt-5',
                ])}
                data-automation-id={heroAutomationIds?.SECONDARYCONTAINER}
            >
                {secondaryComponentsArray.map((component, index) => {
                    return (
                        <span
                            className={dt([
                                'smOnly:flex',
                                'smOnly:w-1/2',
                                isSmallTablet ? '' : 'h-full',
                                index === 0 ? 'smOnly:pr-1' : 'smOnly:pl-1',
                            ])}
                            data-automation-id="at-banner"
                        >
                            {component}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

export default CMHero3;
